import React, { useState, useEffect, useRef } from 'react';
import '../../../styles/contacts.css';
import { Modal } from '@crisellevaldez/speculo-ui';
import { ContactFormData } from '../../../schemas/contact.schema';
import { Contact } from '../../../types/contact.types';
import { ContactEditContent } from './ContactEditContent';
import { useContact } from '../../../hooks/graphql/contacts/useContact';

interface EditContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => Promise<void>;
  contact: Contact | null | { id: string }; // Allow passing just the ID
  onSuccess?: (data: ContactFormData) => void;
  defaultTab?:
    | 'activity'
    | 'emails'
    | 'website'
    | 'calls'
    | 'scheduled_calls'
    | 'saved_properties'
    | 'home_valuations'
    | 'saved_search';
  dateRange?: { from: Date | null; to: Date | null };
}

export const EditContactModal: React.FC<EditContactModalProps> = ({
  isOpen,
  onClose,
  contact,
  onSubmit,
  onSuccess,
  defaultTab = 'activity',
  dateRange,
}) => {
  const [_isEAlertModalOpen, setIsEAlertModalOpen] = useState(false);
  const [localOverride, setLocalOverride] = useState<Partial<Contact> | null>(null);
  const [heroSlot, setHeroSlot] = useState<HTMLDivElement | null>(null);

  const contactId = contact && 'id' in contact ? contact.id : undefined;

  const { contact: gqlContact, error: fetchError } = useContact({
    contactId,
    skip: !isOpen,
  });

  const fullContact = gqlContact
    ? localOverride
      ? ({ ...gqlContact, ...localOverride } as Contact)
      : gqlContact
    : null;

  // Reset local override when modal closes or contact changes
  useEffect(() => {
    if (!isOpen) setLocalOverride(null);
  }, [isOpen]);

  const previousUrlRef = useRef<string | null>(null);

  const handleEAlertClick = () => {
    setIsEAlertModalOpen(true);
  };

  // Update URL when modal opens/closes (preserve query params)
  useEffect(() => {
    if (isOpen && contactId) {
      if (!previousUrlRef.current) {
        previousUrlRef.current = window.location.pathname + window.location.search;
      }
      window.history.replaceState(null, '', `/contact/${contactId}${window.location.search}`);
    } else if (!isOpen && previousUrlRef.current) {
      window.history.replaceState(null, '', previousUrlRef.current);
      previousUrlRef.current = null;
    }
  }, [isOpen, contactId]);

  // Close modal on error
  useEffect(() => {
    if (fetchError) {
      console.error('Error fetching contact data:', fetchError);
      setTimeout(() => onClose(), 2000);
    }
  }, [fetchError, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      className="contact-modal-shell overflow-hidden h-[100dvh] md:h-[calc(100vh-1rem)] 3xl:h-[calc(100vh-3.5rem)]"
    >
      <Modal.Header className="contact-modal-shell !border-foreground/10 !items-start !py-2 md:!py-2.5 3xl:!py-3">
        {/* Reset font-weight/text-size: Modal.Header wraps its child in
            `text-lg font-semibold` (the default modal title styling),
            which would make every line of the portaled hero render bold.
            Forcing font-normal + base text-size keeps the hero visually
            identical to the standalone contact page. */}
        <div ref={setHeroSlot} className="flex-1 min-w-0 font-normal text-base" />
      </Modal.Header>
      <Modal.Body className="flex-1 min-h-0 flex flex-col overflow-y-auto lg:!overflow-hidden contact-modal-shell">
        <div className="lg:flex-1 lg:min-h-0 lg:flex lg:flex-col lg:overflow-hidden">
          <ContactEditContent
            contact={fullContact}
            onSubmit={onSubmit}
            onSuccess={onSuccess}
            defaultTab={defaultTab}
            onEAlertClick={handleEAlertClick}
            showHeader={false}
            heroTarget={heroSlot}
            dateRange={dateRange}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};
